import { gql } from '@apollo/client'

/**
 * Get session fields
 * @internal
 */
export const sessionFieldsQuery: string = `
  id
  customerUid
  ownerInfo {
    slug
  }
  configuration {
    filters {
      attributes
      promotions {
        active
      }
      categories {
        active
      }
      brands {
        active
      }
    }
    stock {
      enabled
      stockThreshold
      hideOutOfStock
      key
      threshold {
        lowWarning {
          id
          warningMessage
          quantity
          enabled
        }
        criticalLowWarning {
          id
          warningMessage
          quantity
          enabled
        }
      }
    }
    promotions {
      enabled
      maximumActive
      autoAdd
      discountFromStock
      disableFreeGoodsAsDiscount
    }
    i18n {
      language
      currencyFormat
      currencySymbol
      currencyCode
      country
      timezone
    }
    workflow
    splitters {
      order {
        active
        maxProduct
      }
    }
    checkoutRules {
      cartConditionPendingMinAmount {
        isActive
        message
        quantity
      }
      cartConditionPendingMaxAmount {
        quantity
        message
        isActive
      }
      cartConditionPendingMinQty {
        quantity
        message
        isActive
      }
      cartConditionPendingMaxQty {
        quantity
        message
        isActive
      }
      cartConditionPendingUserValidation {
        message
        sku
        attribute
        isActive
      }
      cartWarningCheckReturnables {
        message
        sku
        attribute
        isActive
      }
    }
    # A sibling of checkoutRules, never a member of it: the checkout-rule map
    # is a closed set of keys each of which has a handler, and an unhandled key
    # inside it disables that storefront's rule enforcement outright (COM-736).
    # This "order" is a new node on configuration, unrelated to the "order"
    # inside splitters and groupers.
    #
    # NOT YET SAFE TO RELEASE TO PRODUCTION. The merge gate that used to sit
    # beside this selection is gone, because the field it waited for is now
    # served: storefront v3.41.6 is tagged and rolled out to staging, and
    # introspecting storefront-user-stg returns Configuration.order,
    # ConfigOrder { confirmation } and all six leaves. That is what made
    # merging this to main safe -- merging is the staging deploy
    # (upload-staging.yml on push to main), and staging now declares the field.
    #
    # Production is a different question and is still open. Production cinnamon
    # serves the last published GitHub Release, not main, so this selection is
    # not in production simply by virtue of being on main. It arrives the
    # moment someone publishes a Release -- and until the storefront
    # PRODUCTION rollout PR lands, that Release is a fleet-wide outage rather
    # than a degradation. GraphQL validates a whole document against the
    # deployed schema before executing any of it, so an undeclared field is a
    # document-level FieldsOnCorrectType failure: the server returns no data at
    # all rather than this one field arriving null. And sessionFieldsQuery is
    # interpolated into three documents (this one and both getInitialValues
    # documents), so what breaks is the entire boot path plus order detail,
    # order creation and order confirmation, on every storefront in the estate.
    #
    # So: no cinnamon Release until storefront's production rollout is done.
    # That is exactly how Order.subtotal took staging down on 2026-09-07, and
    # the production storefront is still mid-rollout on that same train
    # (COM-822), which is an independent instance of the same blocker.
    #
    # Contrast configuration.i18n.timezone above, which was safe to select the
    # day it was written (COM-816): that field already existed on the deployed
    # SDL. Selecting a field is only ever inert when the server declares it.
    order {
      confirmation {
        required
        pollingTime
        maxAttempts
        timeout
        successStatuses
        failureStatuses
      }
    }
    sessionTtl
    cart {
      quantityAdjustment
    }
    groupers {
      cart {
        session
        active
        product
      }
      order {
        active
        session
        product
      }
    }
    recommendedProducts {
      prefillCart
      suggestedItems
      frequentlyBoughtTogether
    }
    showRecommendations
    notifications {
      type
      enabled
    }
  }
  checkoutRules {
    ruleType
    value
  }
  workflow {
    name
    channel
    channelUid
    userUid
  }
  banners {
    id
    imagesURL
    customerType
    reference
    type
    isActive
    priority
    startDate
    expirationDate
    userAgent
  }
  customer{
    email
    phoneNumber
    code
    customFields
  }
  startedAt
  updatedAt
  status
  finishedAt
  createdAt
  extraIds {
    key
    value
  }
`

/**
 * Get's the string for `session` query.
 * This query gets all existing items in the current session.
 * @internal
 */
const getSessionQuery = gql`
  query session(
    $storefrontName: String!
    $sessionUid: ID!
  ) {
    session(storefrontName: $storefrontName, id: $sessionUid) {
      ${sessionFieldsQuery}
    }
  }
`

export default getSessionQuery
